import { Box, CircularProgress, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BasicPopover from '../../../../sharedComponents/BasicPopover';
import ImageButton from '../../../../sharedComponents/ImageButton';
import * as assets from '../../../../../assets';
import HistoryPopoverData from '../editHistory/EditHistory';
import { getFormattedDateAndTime } from '../../../../staticComponents/staticUtiles';
import { buttonStyle } from '../../dataset/Dataset';
import { HistoryList } from '../History';
import { PartyType, handleEnterOrSpace } from 'components/staticComponents/StaticHtmlGenerator';
import { setFocusQueryInput } from '../../../../../reduxStore/slices/CommonSlice';
import { useAppDispatch } from '../../../../../reduxStore/redux-hooks';

interface ChatHistoryListProps {
  enableLoader: boolean;
  historyData: HistoryList[];
  historyDayIdentifier: string;
  fetchAndProcessConversationMessages: (
    conversationId: string,
    conversationHeading: string,
    party_id: string,
    party_type: string
  ) => void;
}

const ChatHistoryList = ({
  enableLoader,
  historyData,
  historyDayIdentifier,
  fetchAndProcessConversationMessages,
}: ChatHistoryListProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  return (
    <List
      className="dataList_ul"
      sx={{
        overflowY: 'auto',
        padding: 0,
        height: historyDayIdentifier === t('history.lastSevenDaysHistory') ? '340px' : '176px',
      }}
    >
      {enableLoader ? (
        <CircularProgress size={20} sx={{ marginTop: '10px' }} />
      ) : historyData && historyData.length > 0 ? (
        historyData.map((listItem) => {
          const { dateString, timeString } = getFormattedDateAndTime(listItem.last_updated);

          return (
            <Box key={listItem.conversation_id}>
              <Box>
                <ListItem
                  className="listItem"
                  sx={{
                    color: '#256AFF',
                    transition: 'all 300ms ease-in-out',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'block',
                    width: '95%',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                      <ListItemText
                        tabIndex={0}
                        aria-label={`Open conversation ${listItem.title}`}
                        onClick={() => {
                          fetchAndProcessConversationMessages(
                            listItem.conversation_id,
                            listItem.title,
                            listItem.party_id,
                            listItem.party_type
                          );
                          dispatch(setFocusQueryInput(true));
                        }}
                        onKeyDown={(e) =>
                          handleEnterOrSpace(e, () => {
                            fetchAndProcessConversationMessages(
                              listItem.conversation_id,
                              listItem.title,
                              listItem.party_id,
                              listItem.party_type
                            );
                            dispatch(setFocusQueryInput(true));
                          })
                        }
                      >
                        <Typography
                          sx={{
                            width: '275px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {listItem.party_type === PartyType.Agent || listItem.party_type === PartyType.Team
                            ? `${listItem.party_type} : ${listItem.title}`
                            : listItem.title}
                        </Typography>
                      </ListItemText>
                    </Box>
                    <BasicPopover
                      srcComponent={
                        <ImageButton
                          className="buttonShowOnHover"
                          srcFile={assets.dots}
                          tabIndex={0}
                          ariaLabel={`popover ${listItem.title}`}
                          ariaDescribedBy="popoverDescId"
                          style={{
                            ...buttonStyle,
                          }}
                          onKeyDown={(e) => handleEnterOrSpace(e, () => e.currentTarget.click())}
                        />
                      }
                      contentComponent={
                        <HistoryPopoverData
                          conversationHeading={listItem.title}
                          conversationID={listItem.conversation_id}
                        />
                      }
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#7E7E7E', width: '100%' }}>
                    <Typography style={{ fontSize: '13px', marginBottom: '1rem' }}>{dateString}</Typography>
                    <Typography style={{ fontSize: '13px' }}>{timeString}</Typography>
                  </Box>
                </ListItem>
              </Box>
            </Box>
          );
        })
      ) : (
        <Typography>{t('history.noHistoryToShow')}</Typography>
      )}
    </List>
  );
};

export default ChatHistoryList;
